import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { GestaoDistribuicaoEpi } from "../../../../domain/models/gestao_distribuicao_epi";
import { useCallback, useEffect, useMemo } from "react";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import {
  GestaoDistribuicaoEpiForm,
  GestaoDistribuicaoEpiSchema,
} from "../../../schemas/DistribuicaoEpi_lista/gestao_distribuicao_epi";
import { GestaoRecebimentoEpiDropdown } from "../../../../domain/models/gestao_recebimento_epi_dropdown";

import { CargoDropDown } from "../../../../../../common/domain/models/cargo_dropdown";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { DistribuicaoEpiItem } from "../../../../domain/models/gestao_distribuicao_item";
import { GenerateEpiMultiColumns } from "../columns/epi_multi";
import { GridRowId } from "@mui/x-data-grid";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedDistribuicaoEpi: GestaoDistribuicaoEpi | null;
  distribuicaoEpiItem: DistribuicaoEpiItem[] | undefined;
  gestaoRecebimentoEpiDropdown: GestaoRecebimentoEpiDropdown[];
  funcionariosDropdown: FuncionarioDropDown[];
  cargosDropdown: CargoDropDown[];
  centroCustoDropdown: CentroCustoDropDown[];
  centroCustoSaidaDropdown: CentroCustoDropDown[];
  onInclude: (idsEpis: number[], quantidade: number) => void;
  onClose: () => void;
  onSubmit: (form: GestaoDistribuicaoEpiForm) => void;
  handleValidacaoFuncionario: (
    idGestaoDistribuicaoEpi: number | null,
    openState: boolean,
  ) => void;
  onLoadEpisPorCentroCusto: (idCentroCusto: number) => void;
  OnActionQuantidadeChange(
    quantidade: number | null,
    idLinhaGrid: GridRowId,
  ): void;
  onRemoveItem: (idLinhaGrid: GridRowId) => void;
};

export const GestaoDistribuicaoEpiModal = ({
  open,
  saving,
  loading,
  selectedDistribuicaoEpi,
  gestaoRecebimentoEpiDropdown,
  funcionariosDropdown,
  cargosDropdown,
  centroCustoDropdown,
  centroCustoSaidaDropdown,
  distribuicaoEpiItem,
  onInclude,
  onClose,
  onSubmit,
  handleValidacaoFuncionario,
  onLoadEpisPorCentroCusto,
  OnActionQuantidadeChange,
  onRemoveItem,
}: Props) => {
  const hookForm = useForm<GestaoDistribuicaoEpiForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(GestaoDistribuicaoEpiSchema),
  });

  const [idGestaoRecebimentoEpi, idFuncionario, idCentroCustoSaida] =
    hookForm.watch([
      "idGestaoRecebimentoEpi",
      "idFuncionario",
      "idCentroCustoSaida",
    ]);

  useEffect(() => {
    if (idFuncionario) {
      const funcionarioSelecionado = funcionariosDropdown.find(
        (x) => x.idFuncionario === idFuncionario,
      );

      if (funcionarioSelecionado) {
        if (funcionarioSelecionado.idCargo) {
          hookForm.setValue("idCargo", funcionarioSelecionado.idCargo);
        }

        if (funcionarioSelecionado.idCentroCusto) {
          hookForm.setValue(
            "idCentroCusto",
            funcionarioSelecionado.idCentroCusto,
          );
          if (idCentroCustoSaida) onLoadEpisPorCentroCusto(idCentroCustoSaida);
        }
      }
    }
  }, [
    hookForm,
    idFuncionario,
    funcionariosDropdown,
    onLoadEpisPorCentroCusto,
    idCentroCustoSaida,
  ]);

  useEffect(() => {
    if (open) {
      if (selectedDistribuicaoEpi) {
        hookForm.setValue(
          "idFuncionario",
          selectedDistribuicaoEpi.idFuncionario ?? 0,
        );
        hookForm.setValue(
          "quantidade",
          selectedDistribuicaoEpi.quantidade ?? 1,
        );
        hookForm.setValue("dataEntrega", selectedDistribuicaoEpi.dataEntrega);
        hookForm.setValue("dataCadastro", selectedDistribuicaoEpi.dataCadastro);
        hookForm.setValue(
          "idCentroCustoSaida",
          selectedDistribuicaoEpi.idCentroCustoSaida,
        );
      } else {
        hookForm.reset({quantidade: 1});
      }
    }
  }, [open, selectedDistribuicaoEpi, hookForm]);


  const handleIncluir = useCallback(() => {
    const episSelecionados = hookForm.getValues("idGestaoRecebimentoEpi");
    const quantidade = Number(hookForm.getValues("quantidade"));

    if (!episSelecionados || episSelecionados.length === 0) {
      alert("Selecione pelo menos um EPI!");
      return;
    }

    if (!quantidade || quantidade <= 0) {
      alert("A quantidade deve ser maior que zero!");
      return;
    }

    onInclude(episSelecionados, quantidade);

    hookForm.setValue("idGestaoRecebimentoEpi", []);
    hookForm.setValue("quantidade", null as any);
  }, [hookForm, onInclude]);
  
  const isAssinado = !!selectedDistribuicaoEpi?.dataAssinatura; 

  const columnsDef = useMemo(() => {
    return GenerateEpiMultiColumns({
      OnActionQuantidadeChange: OnActionQuantidadeChange,
      onRemoveItem: onRemoveItem, 
      jaEstaAssinado: isAssinado, 
    });
  }, [OnActionQuantidadeChange, onRemoveItem, isAssinado]);

 return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title="Distruibuição de EPI"
      width="850px"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}
    >
      <div className="w-full flex flex-col gap-3 my-4">
        {selectedDistribuicaoEpi?.dataCadastro && (
          <div className="flex flex-row gap-3 w-full">
            <div className="w-1/2">
              <DefaultFormDatePickerInput
                name="dataCadastro"
                label="Data de cadastro"
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>
          </div>
        )}
        <DefaultFormAutocomplete
          name="idFuncionario"
          label="Funcionario"
          required={true}
          options={funcionariosDropdown}
          getOptionDisabled={(x) => !x.canBeSelected}
          getOptionKey={(x) => x.idFuncionario}
          getOptionLabel={(x) => x.nome}
          reactHookForm={hookForm}
        />
        <DefaultFormAutocomplete
          name="idCargo"
          label="Cargo"
          required={true}
          options={cargosDropdown}
          getOptionKey={(x) => x.idCargo ?? 0}
          getOptionLabel={(x) => x.descricao ?? ""}
          reactHookForm={hookForm}
          readOnly={true}
        />

        <DefaultFormAutocomplete
          name="idCentroCusto"
          label="Centro de Custo"
          options={centroCustoDropdown}
          getOptionKey={(x) => x.id ?? 0}
          getOptionLabel={(x) => x.title ?? ""}
          reactHookForm={hookForm}
          readOnly={true}
        />

        <DefaultFormAutocomplete
          name="idCentroCustoSaida"
          label="Centro de Custo Saída"
          options={centroCustoSaidaDropdown}
          getOptionKey={(x) => x.id ?? 0}
          getOptionLabel={(x) => x.title ?? ""}
          reactHookForm={hookForm}
          required={true}
        />

        <DefaultFormMultiAutocomplete
          name="idGestaoRecebimentoEpi"
          label="Nome do EPI"
          required={false} // 👈 Mudou para false no form, pois a validação é no "Incluir"
          options={gestaoRecebimentoEpiDropdown}
          getOptionKey={(x) => x.idGestaoRecebimentoEpi}
          getOptionLabel={(x) => x.nomeEpi ?? ""}
          reactHookForm={hookForm}
        />

        <div className="flex flex-row gap-3 w-full items-end">
          <div className="flex-1">
            <FormMaskedInput
              name="quantidade"
              label="Quantidade"
              required={false}
              mask="00000000"
              maskPlaceholder="        "
              placeholderChar=" "
              reactHookForm={hookForm}
            />
          </div>
          <div className="flex-1">
            <DefaultFormDatePickerInput
              name="dataEntrega"
              label="Data de entrega"
              readOnly={true}
              reactHookForm={hookForm}
            />
          </div>
          {/* 👇 Novo Botão Incluir */}
          <div className="w-[120px]">
            <PrimaryButton
              type="button" // 👈 Extremamente importante para não salvar a tela inteira
              buttonColor="primary-green"
              onClick={handleIncluir}
            >
              Incluir
            </PrimaryButton>
          </div>
        </div>

        <span className="text-red-700 text-xs">
          * A Data de entrega é preenchida automaticamente após a assinatura.
        </span>

        <StyledDataGrid
          tableName=""
          disableColumnMenu={false}
          showToolbar={true}
          disableRowSelectionOnClick={true}
          getRowId={(row: DistribuicaoEpiItem) => row.idGestaoDistribuicaoEpiItem ?? 0}
          columns={columnsDef}
          checkboxSelection={false}
          rows={distribuicaoEpiItem ?? []}
          rowCount={distribuicaoEpiItem ? distribuicaoEpiItem.length : 0}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
        />
      </div>
      <div className="w-[120px]">
        {selectedDistribuicaoEpi?.idGestaoDistribuicaoEpi ? (
          <PrimaryButton
            buttonColor="red"
            onClick={() =>
              handleValidacaoFuncionario(
                selectedDistribuicaoEpi.idGestaoDistribuicaoEpi,
                true,
              )
            }
          >
            Assinar
          </PrimaryButton>
        ) : (
          <> </>
        )}
      </div>
    </BaseFormControlledModal>
  );
};
